import type { DiscordRefusal } from '../../api/changelog';
import { ApiError, OfflineError } from '../../api/http';
import { ca } from '../../i18n/ca';
import { fmtDay, fmtMoment } from '../dates';

/** The error string an admin sees for a failed call: the API's code, translated, or the generic one. */
export function errorText(err: unknown): string {
  if (err instanceof OfflineError) return ca.errors.offline;
  return err instanceof ApiError ? (ca.errors[err.code] ?? ca.errors.unknown) : ca.errors.unknown;
}

/**
 * Discord's refusal in Catalan, naming the one thing to change.
 *
 * A bot that will not post has failed in one of three places — the token, the
 * invitation to the server, the channel's own permissions — and Discord says
 * which every time. Null when there is nothing to explain: it went out, or
 * nobody tried.
 */
export function discordRefusalText(refusal: DiscordRefusal | null | undefined): string | null {
  if (!refusal || refusal.sent) return null;
  const t = ca.discordRefusals;
  if (refusal.code > 0 && t.codes[String(refusal.code)]) return t.codes[String(refusal.code)];
  // Discord answers 401 with code 0: the token, and nothing else, is wrong.
  if (refusal.status === 401) return t.unauthorised;
  if (refusal.status === 429) return t.rateLimited;
  if (refusal.status === 0) return t.unreachable;
  return t.other(refusal.status, refusal.text ?? '');
}

export const fmtWhen = (iso: string) => fmtMoment(iso);

/** The day alone, for a fact whose hour says nothing: the last day a player played. */
export { fmtDay };

/** The day a person names it by, for the heading a log's rows sit under. */
export const fmtDayLong = (iso: string) =>
  new Date(iso).toLocaleDateString('ca', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/** The hour alone, for a row that already sits under the day it happened on. */
export const fmtClock = (iso: string) =>
  new Date(iso).toLocaleTimeString('ca', { hour: '2-digit', minute: '2-digit' });

/** The tabs of the admin page; the active one lives in `?tab=` so a reload keeps it. */
export const ADMIN_TABS = [
  'accounts',
  'invitations',
  'players',
  'integrations',
  'cba',
  'recipes',
  'catalog',
  'activity',
] as const;
export type AdminTab = (typeof ADMIN_TABS)[number];

export const ADMIN_TAB_ICONS: Record<AdminTab, string> = {
  accounts: 'people',
  invitations: 'envelope-paper',
  players: 'controller',
  integrations: 'plug',
  cba: 'sliders',
  recipes: 'journal-check',
  catalog: 'grid',
  activity: 'clock-history',
};

export function isAdminTab(value: string | null): value is AdminTab {
  return (ADMIN_TABS as readonly string[]).includes(value ?? '');
}
